import { useState, useCallback, useMemo } from 'react';
import { BACKEND_URL, getAuthHeaders } from './dashboardUtils';
import { getFromLocalStorage, saveToLocalStorage } from '../mock';
import { toast } from '../hooks/use-toast';

/**
 * Custom hook: AdminDashboard'daki tüm API handler fonksiyonlarını içerir.
 * Davranış değişikliği yok — sadece ayrıştırma.
 */
const useAdminHandlers = ({
  navigate, jobs, setJobs, customers, setCustomers, staffList, setStaffList,
  setActivityLogs, setNotifications, setUnreadCount,
  setVehicleHistory, setShowVehicleHistory, selectedBranchId, loadData,
}) => {

  const loadLogs = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/activity-logs?limit=100`, { headers: getAuthHeaders() });
      if (res.ok) { const d = await res.json(); setActivityLogs(d.logs || []); }
    } catch (e) { console.error('Load logs error:', e); }
  }, [setActivityLogs]);

  const loadNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/notifications?limit=30`, { headers: getAuthHeaders() });
      if (res.ok) {
        const d = await res.json();
        setNotifications(d.notifications || []);
        setUnreadCount(d.unreadCount || 0);
      }
    } catch (e) { console.error('Load notifications error:', e); }
  }, [setNotifications, setUnreadCount]);

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/jobs/${jobId}`, {
        method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ status: newStatus })
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      const d = await res.json();
      setJobs(p => p.map(j => j.id === jobId ? d.job : j));
      toast({ title: "Durum Güncellendi" });
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleAddPayment = async (selectedJob, paymentAmount, paymentMethod, paymentNote, paymentType, resetPayment) => {
    if (!selectedJob || !paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast({ title: "Hata", description: "Geçerli bir tutar girin.", variant: "destructive" }); return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/payments`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ jobId: selectedJob.id, amount: parseFloat(paymentAmount), method: paymentMethod, note: paymentNote || 'Ödeme', paymentType })
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      const d = await res.json();
      setJobs(p => p.map(j => j.id === selectedJob.id ? d.job : j));
      let desc = `${parseFloat(paymentAmount)} TL ödeme eklendi.`;
      if (d.bonus_earned > 0) desc += ` ${d.bonus_earned} TL bonus!`;
      toast({ title: "Ödeme Alındı", description: desc });
      resetPayment();
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleDeleteCustomer = async (id) => {
    if (!window.confirm('Bu müşteri arşivlenecek. Emin misiniz?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/customers/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      setCustomers(p => p.filter(c => c.id !== id));
      toast({ title: "Müşteri Arşivlendi" });
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleDeleteJob = async (id) => {
    if (!window.confirm('Bu iş arşivlenecek. Emin misiniz?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/jobs/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      setJobs(p => p.filter(j => j.id !== id));
      toast({ title: "İş Arşivlendi" });
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleDeleteStaff = async (id) => {
    if (!window.confirm('Bu personel devre dışı bırakılacak. Emin misiniz?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/staff/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      setStaffList(p => p.filter(s => s.id !== id));
      toast({ title: "Personel Devre Dışı" });
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleViewVehicleHistory = async (vehicleId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/vehicles/${vehicleId}/history`, { headers: getAuthHeaders() });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      const data = await res.json();
      setVehicleHistory(data); setShowVehicleHistory(true);
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleSendActivation = async (customerId, customerName) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/customers/${customerId}/send-activation`, { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Hata');
      toast({ title: "Davet Gonderildi", description: data.email_sent ? `${customerName} adresine aktivasyon emaili gonderildi.` : `Davet kaydedildi ancak email gonderilemedi.` });
      loadData();
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleResendActivation = async (customerId, customerName) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/customers/${customerId}/resend-activation`, { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Hata');
      toast({ title: data.sent ? "Mail Gonderildi" : "Gonderilemedi", description: data.message, variant: data.sent ? "default" : "destructive" });
      loadData();
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleAdminSetPassword = async (customerId, customerName) => {
    const pwd = window.prompt(`"${customerName}" icin gecici sifre belirleyin (en az 6 karakter):`);
    if (!pwd) return;
    if (pwd.length < 6) { toast({ title: "Hata", description: "Sifre en az 6 karakter olmali", variant: "destructive" }); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/customers/${customerId}/admin-set-password`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ password: pwd }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Hata');
      toast({ title: "Sifre Belirlendi", description: data.message });
      loadData();
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleWhatsAppActivation = async (customerId, customerName) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/customers/${customerId}/whatsapp-activation`, { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Hata');
      window.open(data.waLink, '_blank');
      toast({ title: "WhatsApp Acildi", description: `${customerName} icin aktivasyon linki WhatsApp'ta gonderilmeye hazir.` });
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleVehicleSearch = async (query, setVehicleSearchQuery, setVehicleSearchResults) => {
    setVehicleSearchQuery(query);
    if (!query || query.length < 2) { setVehicleSearchResults(null); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/vehicles/search?q=${encodeURIComponent(query)}`, { headers: getAuthHeaders() });
      if (res.ok) { const d = await res.json(); setVehicleSearchResults(d.vehicles || []); }
    } catch (e) { console.error('Vehicle search error:', e); }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/notifications/read`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ markAll: true }) });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) { console.error('Mark all read error:', e); }
  };

  const handleEditVehicle = async (vehicleData, editingVehicle, setEditingVehicle) => {
    if (!editingVehicle) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/vehicles/${editingVehicle.id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(vehicleData) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Hata');
      toast({ title: "Arac Guncellendi", description: `${d.vehicle?.plate || ''} basariyla guncellendi.` });
      setEditingVehicle(null);
      loadData();
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleDeleteVehicle = async (vehicleId, plate) => {
    if (!window.confirm(`"${plate}" aracini silmek/arsivlemek istediginize emin misiniz?`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/vehicles/${vehicleId}`, { method: 'DELETE', headers: getAuthHeaders() });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Hata');
      toast({ title: d.action === 'archived' ? 'Arac Arsivlendi' : 'Arac Silindi', description: d.message });
      loadData();
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleTransferVehicle = async (transferVehicle, transferTargetId, setTransferVehicle, setTransferTargetId) => {
    if (!transferVehicle || !transferTargetId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/vehicles/${transferVehicle.id}/transfer`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ targetCustomerId: transferTargetId }) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Hata');
      toast({ title: "Arac Transfer Edildi", description: `${d.transfer?.plate}: ${d.transfer?.from} → ${d.transfer?.to}` });
      setTransferVehicle(null); setTransferTargetId('');
      loadData();
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleImpersonateCustomer = async (customerId, customerName) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/customers/${customerId}/impersonate`, { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Hata');
      const adminToken = getFromLocalStorage('accessToken');
      const adminUser = getFromLocalStorage('currentUser');
      saveToLocalStorage('adminToken', adminToken);
      saveToLocalStorage('adminUser', adminUser);
      saveToLocalStorage('impersonating', true);
      saveToLocalStorage('accessToken', data.access_token);
      saveToLocalStorage('currentUser', data.customer);
      toast({ title: "Musteri Gorunumu", description: `${customerName} olarak goruntuleniyor` });
      navigate('/dashboard');
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleQuickProgress = async (jobId, newStatus) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/jobs/${jobId}/progress`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ newStatus }) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Hata');
      setJobs(prev => prev.map(j => j.id === jobId ? d.job : j));
      toast({ title: "Durum Guncellendi", description: d.message });
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleArchiveJob = async (jobId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/jobs/${jobId}/archive`, { method: 'POST', headers: getAuthHeaders() });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      setJobs(prev => prev.filter(j => j.id !== jobId));
      toast({ title: "Arşivlendi", description: "İş arşivlendi." });
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  return {
    loadLogs, loadNotifications,
    handleStatusChange, handleAddPayment,
    handleDeleteCustomer, handleDeleteJob, handleDeleteStaff,
    handleViewVehicleHistory, handleSendActivation, handleResendActivation,
    handleAdminSetPassword, handleWhatsAppActivation,
    handleVehicleSearch, handleMarkAllRead,
    handleEditVehicle, handleDeleteVehicle, handleTransferVehicle,
    handleImpersonateCustomer, handleQuickProgress, handleArchiveJob,
  };
};

export default useAdminHandlers;
